import {useAppDispatch, useAppSelector} from "../../reducers/hooks";
import React, {MouseEvent, useCallback, useEffect, useMemo, useState} from "react";
import {deleteElement, getAllElement, MultimediaElement} from "../../reducers/Multimedia/MultimediaReducer";
import {ColumnDef, ColumnFiltersState} from "@tanstack/react-table";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import {Trash} from "iconsax-react";
import Grid from "@mui/material/Grid";
import ElementModalGeneric from "../../sections/modals/exampleElementModal/ElementModalGeneric";
import AlertExampleElementDelete from "../../sections/modals/exampleElementModal/AlertExampleElementDelete";
import TablePagination from "@mui/material/TablePagination";
import {debounce} from "lodash";
import {openSnackbar, SnackbarState} from "reducers/snackbar/snackbarReducer";
import {FormattedMessage, useIntl} from "react-intl";
import Avatar from "@mui/material/Avatar";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import {DogElementForm} from "../../sections/forms/Dog/DogElementForm";
import BasicTableCustom from "../../components/tables/BasicTableCustom";
import CustomLoader from "../../components/CustomLoader";

export default function Multimedia() {
    const dispatch = useAppDispatch();
    const intl = useIntl();
    const {elements, currentPage, totalRows, loading, sort} = useAppSelector(state => state.multimediaElement);
    const [open, setOpen] = useState<boolean>(false);
    const [selectedElement, setSelectedElement] = useState<MultimediaElement | null>(null);
    const [selectedDeleteElement, setSelectedDeleteElement] = useState<MultimediaElement | null>(null);
    const [deleteAlertOpen, setDeleteAlertOpen] = useState<boolean>(false);
    const [filters, setFilters] = useState<ColumnFiltersState | null>(null)

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10); // Numero di righe per pagina
    const [searchTerms, setSearchTerms] = useState({
        name: "",
        type: "",
        entityType: "",
        entityId: "",
    });

    const handleSearchChange = useCallback(
        debounce((key: string, value: string) => {
            setSearchTerms(prev => ({
                ...prev,
                [key]: value,
            }));
        }),
        []
    );


    // Filtro i dati in base ai criteri di ricerca

    const columns = useMemo<ColumnDef<MultimediaElement>[]>(() => [
        {
            header: 'Anteprima',
            enableColumnFilter: false,
            accessorKey: 'base64EncodedContent',
            cell: ({row}) => {
                const base64 = row.original.base64EncodedContent;
                const fileType = row.original.type;

                if (base64) {
                    if (fileType?.startsWith("PNG") || fileType?.startsWith("JP")) {
                        // Anteprima per JPG e PNG
                        return (
                            <Avatar
                                variant="rounded"
                                src={base64}
                                alt="Anteprima Immagine"
                                sx={{
                                    width: 100,
                                    height: 100,
                                    objectFit: 'cover',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                }}
                            />
                        );
                    } else if (fileType === "PDF") {
                        // Anteprima per PDF
                        return (
                            <Card
                                variant="outlined"
                                sx={{
                                    width: 100,
                                    height: 120,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    backgroundColor: 'background.paper',
                                }}
                            >
                                <iframe
                                    src={base64}
                                    title="Anteprima PDF"
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        border: 'none',
                                    }}
                                />
                            </Card>
                        );
                    } else if (
                        fileType === "DOC" ||
                        fileType === "DOCX"
                    ) {
                        // Anteprima per DOC e DOCX
                        return (
                            <Card
                                variant="outlined"
                                sx={{
                                    width: 100,
                                    height: 120,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    backgroundColor: 'background.default',
                                    textAlign: 'center',
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    color="textSecondary"
                                    sx={{
                                        wordWrap: 'break-word',
                                        maxWidth: '80px',
                                        fontSize: '0.75rem',
                                    }}
                                >
                                    {row.original.name || "DOC/DOCX"}
                                </Typography>
                            </Card>
                        );
                    } else {
                        // Placeholder per tipi di file non supportati
                        return (
                            <Box
                                sx={{
                                    width: 100,
                                    height: 120,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: '1px dashed',
                                    borderColor: 'divider',
                                    backgroundColor: 'background.default',
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    color="textSecondary"
                                    sx={{textAlign: 'center', fontSize: '0.75rem'}}
                                >
                                    Tipo non supportato
                                </Typography>
                            </Box>
                        );
                    }
                }
                return (
                    <Typography variant="caption" color="textSecondary">
                        Non disponibile
                    </Typography>
                );
            }
        },
        {
            header: intl.formatMessage({id: "Name"}),
            accessorKey: 'name',
            enableColumnFilter: false

        },
        {
            header: intl.formatMessage({id: "type"}),
            accessorKey: 'type',
            enableColumnFilter: false


        },
        {
            header: intl.formatMessage({id: "entityType"}),
            accessorKey: 'entityType',
            enableColumnFilter: false
        },
        {
            header: intl.formatMessage({id: "entityId"}),
            accessorKey: 'entityId',
            enableColumnFilter: false

        },
        {
            header: () => <FormattedMessage id="Actions" defaultMessage="Actions"/>,
            enableColumnFilter: false,
            accessorKey: 'Actions',
            meta: {
                className: 'cell-right'
            },
            cell: ({row}) => (
                <Stack direction="row" alignItems="center" justifyContent="center" spacing={0}>
                    <Tooltip title="Delete">
                        <IconButton
                            color="error"
                            onClick={(e: MouseEvent<HTMLButtonElement>) => {
                                e.stopPropagation();
                                setSelectedDeleteElement(row.original);
                                setDeleteAlertOpen(true);
                            }}
                        >
                            <Trash/>
                        </IconButton>
                    </Tooltip>
                </Stack>
            )
        }
    ], [searchTerms]);

    // Effettua la chiamata per ottenere i dati all'inizializzazione o quando la pagina cambia
    useEffect(() => {
        dispatch(getAllElement({
            pageableDto: {page, size: rowsPerPage, sort: ['name', 'asc']}
        }));
    }, [dispatch, page, rowsPerPage]);


    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0); // Resetta alla prima pagina
    };

    const handleChangePage = (event: MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        setPage(newPage);
    };

    const columnFiltersState = (values: ColumnFiltersState) => {
        setPage(0)
        setFilters(values)
    };

    const onDeleteSuccess = () => {
        //effettuare la dispatch della getAll e della visualizzazione popup
        dispatch(getAllElement({
            pageableDto: {page: currentPage, size: rowsPerPage, sort}
        }))

        openSnackbar(
            {
                open: true,
                message: 'Element deleted successfully',
                variant: 'alert',
                alert: {
                    color: 'success'
                }
            } as SnackbarState
        )

    };

    const handleDelete = (_id: string) => {
        dispatch(deleteElement({id: _id, callback: onDeleteSuccess}));
    };
    const handleModalOpen = (open: boolean) => {
        setOpen(open);
        setSelectedElement(null);
    };

    const {formatMessage} = useIntl();
    const getTableTitle = () => {
        return formatMessage({id: "MultimediaTitle", defaultMessage: "Multimedia"});
    };

    return (
        <>
            <CustomLoader open={loading} message="Caricamento in corso..."/>
            <Grid container>
                <Grid item xs={12}>
                    <BasicTableCustom
                        columns={columns}
                        data={elements}
                        title={getTableTitle()}
                        striped
                        modalToggler={() => handleModalOpen(!open)}
                        onFilterChange={columnFiltersState}
                        manualFiltering={true}
                    />

                    <TablePagination
                        component="div"
                        count={totalRows}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />

                    <ElementModalGeneric
                        open={open}
                        setOpen={handleModalOpen}
                        element={selectedElement}
                        // decidere se fare una modale dove si dice che non si possono aggiungere
                        FormComponent={DogElementForm}
                    />
                    {selectedDeleteElement && (
                        <AlertExampleElementDelete
                            id={selectedDeleteElement._id}
                            title={selectedDeleteElement.name}
                            open={deleteAlertOpen}
                            handleClose={() => setDeleteAlertOpen(false)}
                            onDelete={(_id) => handleDelete(_id)}
                        />
                    )}
                </Grid>
            </Grid>
        </>
    );
}
